import { useEffect, useRef, useState } from 'react';
import {
  addMonths,
  endOfDay,
  endOfMonth,
  endOfYear,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfYear,
  subMonths
} from 'date-fns';
import Button from './Button';
import Calendar from './Calendar';
import Icon from './Icon';
import styles from './PeriodPicker.module.css';

export type PeriodPickerProps = {
  inicio: Date;
  fim: Date;
  onChange: (inicio: Date, fim: Date) => void;
};

type Preset = {
  id: string;
  rotulo: string;
  calcular: () => [Date, Date];
};

const criarPresets = (): Preset[] => {
  const hoje = new Date();
  return [
    { id: 'este-mes', rotulo: 'Este mês', calcular: () => [startOfMonth(hoje), endOfMonth(hoje)] },
    {
      id: 'mes-passado',
      rotulo: 'Mês passado',
      calcular: () => {
        const mes = subMonths(hoje, 1);
        return [startOfMonth(mes), endOfMonth(mes)];
      }
    },
    {
      id: 'ultimos-3-meses',
      rotulo: 'Últimos 3 meses',
      calcular: () => [startOfMonth(subMonths(hoje, 2)), endOfMonth(hoje)]
    },
    {
      id: 'ultimos-6-meses',
      rotulo: 'Últimos 6 meses',
      calcular: () => [startOfMonth(subMonths(hoje, 5)), endOfMonth(hoje)]
    },
    { id: 'este-ano', rotulo: 'Este ano', calcular: () => [startOfYear(hoje), endOfYear(hoje)] },
    {
      id: 'ultimos-12-meses',
      rotulo: 'Últimos 12 meses',
      calcular: () => [startOfMonth(subMonths(hoje, 11)), endOfMonth(hoje)]
    }
  ];
};

const dataCurta = (data: Date, comAno: boolean) => {
  const mes = data.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
  return `${data.getDate()} ${mes}${comAno ? ` ${data.getFullYear() % 100}` : ''}`;
};

/**
 * Stepper de mês `‹ julho 2026 ›` + menu de presets (com período personalizado).
 * Emite sempre inicio 00:00:00.000 e fim 23:59:59.999.
 */
export default function PeriodPicker({ inicio, fim, onChange }: PeriodPickerProps) {
  const [aberto, setAberto] = useState(false);
  const raizRef = useRef<HTMLDivElement>(null);

  const presets = criarPresets();
  const presetAtual = presets.find((preset) => {
    const [presetInicio, presetFim] = preset.calcular();
    return isSameDay(presetInicio, inicio) && isSameDay(presetFim, fim);
  });

  // Rótulo central: nome do mês quando o range é exatamente um mês-calendário.
  const ehMesCheio = isSameDay(inicio, startOfMonth(inicio)) && isSameDay(fim, endOfMonth(inicio));
  const anosDiferentes = inicio.getFullYear() !== fim.getFullYear();
  const rotuloCentral = ehMesCheio
    ? `${inicio.toLocaleDateString('pt-BR', { month: 'long' })} ${inicio.getFullYear()}`
    : `${dataCurta(inicio, anosDiferentes)} a ${dataCurta(fim, anosDiferentes)}`;

  const emitir = (novoInicio: Date, novoFim: Date) => {
    onChange(startOfDay(novoInicio), endOfDay(novoFim));
  };

  const passoMes = (delta: number) => {
    const base = addMonths(startOfMonth(inicio), delta);
    emitir(base, endOfMonth(base));
  };

  // Fecha em clique fora e Esc.
  useEffect(() => {
    if (!aberto) return;
    const aoClicarFora = (evento: MouseEvent) => {
      if (raizRef.current && !raizRef.current.contains(evento.target as Node)) setAberto(false);
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAberto(false);
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto]);

  const aplicarPreset = (preset: Preset) => {
    const [presetInicio, presetFim] = preset.calcular();
    emitir(presetInicio, presetFim);
    setAberto(false);
  };

  const aplicarPersonalizado = (novoInicio: Date, novoFim?: Date) => {
    if (!novoFim) return;
    emitir(novoInicio, novoFim);
    setAberto(false);
  };

  return (
    <div className={styles.raiz} ref={raizRef}>
      <div className={styles.stepper}>
        <button
          type="button"
          className={styles.seta}
          onClick={() => passoMes(-1)}
          aria-label="Mês anterior"
        >
          <Icon nome="chevron_left" tamanho={20} />
        </button>
        <span className={styles.rotuloMes}>{rotuloCentral}</span>
        <button
          type="button"
          className={styles.seta}
          onClick={() => passoMes(1)}
          aria-label="Próximo mês"
        >
          <Icon nome="chevron_right" tamanho={20} />
        </button>
      </div>

      <Button
        variante="suave"
        tamanho="sm"
        icone="expand_more"
        onClick={() => setAberto((atual) => !atual)}
      >
        {presetAtual?.rotulo ?? 'Personalizado'}
      </Button>

      {aberto && (
        <div className={styles.painel}>
          {presets.map((preset) => {
            const selecionado = preset.id === presetAtual?.id;
            return (
              <button
                key={preset.id}
                type="button"
                className={`${styles.opcao} ${selecionado ? styles.selecionada : ''}`}
                onClick={() => aplicarPreset(preset)}
              >
                <span className={styles.rotuloOpcao}>{preset.rotulo}</span>
                {selecionado && (
                  <span className={styles.check}>
                    <Icon nome="check" tamanho={16} />
                  </span>
                )}
              </button>
            );
          })}
          <div className={styles.secao}>
            <span className={styles.secaoTitulo}>Personalizado</span>
            <Calendar modo="range" inicio={inicio} fim={fim} onChange={aplicarPersonalizado} />
          </div>
        </div>
      )}
    </div>
  );
}
